import * as React from 'react';
import { cn } from '../../utils';
import { Breadcrumb, type BreadcrumbItem } from '../navigation/breadcrumb';

export interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  breadcrumb?: BreadcrumbItem[];
  title: string;
  /** One sentence on what the page is for — not a restatement of on-screen numbers. */
  subtitle?: string;
  /** Extra content under the title (chips, counts). Rendered below `subtitle`. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

/**
 * The one page title block on the platform.
 *
 * Two details are load-bearing (see DESIGN-RULES §5):
 * - Actions are centred against the **whole** block, not pinned to the top of
 *   the heading. Top-aligning them is what makes a primary button read as
 *   floating too high next to a two-line title.
 * - The title uses the page-title step of the type scale; it is the largest
 *   text on the screen and every page agrees on its size.
 */
export function PageHeader({
  breadcrumb,
  title,
  subtitle,
  meta,
  actions,
  className,
  ...props
}: PageHeaderProps) {
  return (
    <div className={cn('space-y-2', className)} {...props}>
      {breadcrumb && breadcrumb.length > 0 && <Breadcrumb items={breadcrumb} />}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
        <div className="min-w-0 max-w-2xl">
          <h1 className="text-[26px] font-bold leading-tight tracking-[-0.03em] text-foreground sm:text-[30px]">
            {title}
          </h1>
          {subtitle && (
            <p
              title={subtitle}
              className="mt-1.5 truncate text-[13.5px] leading-relaxed text-muted-foreground"
            >
              {subtitle}
            </p>
          )}
          {meta && <div className="mt-1.5">{meta}</div>}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 self-start sm:shrink-0 sm:self-auto">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
